import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createApi } from "../api/client";
import type { DashboardState } from "../types";

const POLL_MS = 5000;

export interface DashboardActions {
  runZone: (id: string, minutes: number) => Promise<void>;
  stopZone: (id: string) => Promise<void>;
  stopAll: () => Promise<void>;
  setDuration: (id: string, minutes: number) => Promise<void>;
  setEnabled: (id: string, enabled: boolean) => Promise<void>;
  setRainDelay: (paused: boolean) => Promise<void>;
  sync: () => Promise<void>;
}

export interface UseDashboard {
  state: DashboardState | null;
  error: string | null;
  syncing: boolean;
  /** In-flight commands keyed by `${verb}:${id}` (e.g. "run:z6", "stop:z6", "rainDelay"). */
  pending: Record<string, boolean>;
  actions: DashboardActions;
}

export function useDashboard(): UseDashboard {
  const api = useMemo(() => createApi(), []);
  const [state, setState] = useState<DashboardState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const alive = useRef(true);
  const stateRef = useRef<DashboardState | null>(null);
  stateRef.current = state;

  const refresh = useCallback(async () => {
    try {
      const s = await api.getState();
      if (alive.current) {
        setState(s);
        setError(null);
      }
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : "connection lost");
    }
  }, [api]);

  useEffect(() => {
    alive.current = true;
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => {
      alive.current = false;
      clearInterval(t);
    };
  }, [refresh]);

  // Run a command with optimistic in-flight feedback so controls acknowledge
  // immediately (disabled + spinner) instead of waiting a poll cycle to react.
  const wrap = useCallback(
    (key: string, fn: () => Promise<void>) =>
      async () => {
        if (alive.current) setPending((p) => ({ ...p, [key]: true }));
        try {
          await fn();
        } catch (e) {
          setError(e instanceof Error ? e.message : "command failed");
        } finally {
          await refresh();
          if (alive.current)
            setPending((p) => {
              const n = { ...p };
              delete n[key];
              return n;
            });
        }
      },
    [refresh],
  );

  const actions: DashboardActions = useMemo(
    () => ({
      runZone: (id, minutes) => wrap(`run:${id}`, () => api.runZone(id, minutes))(),
      stopZone: (id) => wrap(`stop:${id}`, () => api.stopZone(id))(),
      stopAll: () =>
        wrap("stop:all", async () => {
          const zones = stateRef.current?.zones ?? [];
          const active = zones.filter((z) => z.state === "watering" || z.state === "queued");
          await Promise.all(active.map((z) => api.stopZone(z.id)));
        })(),
      setDuration: (id, minutes) => wrap(`dur:${id}`, () => api.setDuration(id, minutes))(),
      setEnabled: (id, enabled) => wrap(`enable:${id}`, () => api.setEnabled(id, enabled))(),
      setRainDelay: (paused) => wrap("rainDelay", () => api.setRainDelay(paused))(),
      sync: async () => {
        setSyncing(true);
        try {
          await api.sync();
        } catch (e) {
          setError(e instanceof Error ? e.message : "sync failed");
        } finally {
          await refresh();
          setSyncing(false);
        }
      },
    }),
    [api, wrap, refresh],
  );

  return { state, error, syncing, pending, actions };
}
